<template>
  <div class="card">
    <h2>Sales</h2>
    <table class="salas">
      <tbody>
        <tr v-for="fila of filasSala" :key="fila.sala.Id">
          <td class="sala__nombre-cell">
            <span class="sala__nombre">{{ fila.sala.Nombre }}</span>
            <span
              v-if="fila.dotClass"
              class="sala__dot"
              :class="fila.dotClass"
            ></span>
          </td>
          <td class="sala__vetlla-cell">
            <select class="sala__vetlla" v-model="fila.sala.IdVetlla">
              <option :value="-1">-</option>
              <option
                v-for="opcion of opcionesVetlla"
                :value="opcion.vetlla.Id"
                :disabled="opcion.disabled"
              >
                {{ opcion.vetlla.NombreDifunto }} - #{{ opcion.vetlla.Id }}
              </option>
            </select>
          </td>
          <td class="sala__actions-cell">
            <button
              class="sala-button sala-button--primary"
              @click="goPresentation(fila.sala.Id)"
              title="Veure presentació a la pantalla"
            >
              <font-awesome-icon icon="tv" />
            </button>
          </td>
          <td class="sala__status-cell">
            <span
              v-if="fila.statusLabel"
              class="sala__status"
              :class="fila.statusClass"
              :title="fila.statusFullText"
            >
              {{ fila.statusLabel
              }}<template v-if="fila.statusCompact">
                · {{ fila.statusCompact }}</template
              >
            </span>
            <span
              v-if="fila.ipsLine"
              class="sala__ips"
              :title="fila.ipsTooltip"
            >
              {{ fila.ipsLine }}
            </span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import axios from "axios";
import CustomSwal from "../custom-swal.ts";
import { Vetlla, Sala, SalaStatus, HeartbeatEntry } from "../types/vetlla.ts";
import useErpApiClient from "../erp-api-client";
import { dateFromFechaString } from "../helpers";
import { useRouter } from "vue-router";

export default defineComponent({
  setup() {
    const router = useRouter();
    const erpClient = useErpApiClient();

    let vetllas: Vetlla[] = [];
    let vetllasCurso = ref<Vetlla[]>([]);
    let vetllasAsignadas = ref<number[]>([]);

    const salas = ref<Sala[]>([]);
    const salasStatus = ref<SalaStatus[]>([]);

    let statusIntervalId: ReturnType<typeof setInterval>;

    const isVetllaExpired = (fechaFin: string) => {
      const now = new Date().getTime();
      const fechaFinDate = dateFromFechaString(fechaFin);
      const fechaFinMs = fechaFinDate.getTime();
      return fechaFinMs < now;
    };

    const updateExpiredVetllasInSales = () => {
      for (const sala of salas.value) {
        if (sala.IdVetlla !== -1) {
          const vetlla = vetllas.find((v) => v.Id === sala.IdVetlla);
          if (vetlla && isVetllaExpired(vetlla.FechaFin)) {
            sala.IdVetlla = -1;
            erpClient
              .request(() =>
                axios.post("/api/sala/update", {
                  IdSala: sala.Id,
                  IdVetlla: sala.IdVetlla,
                }),
              )
              .catch(() => {
                CustomSwal.fire({
                  icon: "error",
                  title:
                    "No s'ha pogut actualitzar la vetlla per aquesta sala. Posi's en contacte amb els administradors de l'aplicació.",
                });
              });
          }
        }
      }
    };

    const statusMap = computed(() => {
      const map = new Map<number, SalaStatus>();
      for (const s of salasStatus.value) {
        map.set(s.id, s);
      }
      return map;
    });

    const filasSala = computed(() => {
      return salas.value.map((sala) => {
        const s = statusMap.value.get(sala.Id);
        let dotClass = "";
        let statusLabel = "";
        let statusClass = "";
        let statusCompact = "";
        let statusFullText = "";
        let ipsLine = "";
        let ipsTooltip = "";

        if (s) {
          if (s.pollStatus === "error") {
            dotClass = "sala__dot--red";
            statusLabel = "Error";
            statusClass = "sala__status--error";
            statusCompact = s.pollMessage;
            statusFullText = s.pollMessage;
          } else if (s.pollStatus === "never") {
            dotClass = "sala__dot--gray";
            statusLabel = "Sense senyal";
            statusClass = "sala__status--sin-senyal";
          } else if (s.secondsAgo <= 10) {
            dotClass = "sala__dot--green";
            statusLabel = "Activa";
            statusClass = "sala__status--activa";
            statusCompact = buildCompactText(s);
            statusFullText = buildFullText(s);
          } else {
            dotClass = "sala__dot--yellow";
            statusLabel = "Lenta";
            statusClass = "sala__status--lenta";
            statusCompact = buildCompactText(s);
            statusFullText = buildFullText(s);
          }

          if (s.lastIPs && s.lastIPs.length > 0) {
            ipsLine = buildIpsLine(s.lastIPs);
            ipsTooltip = buildIpsTooltip(s.lastIPs);
          }
        } else if (sala.IdVetlla === -1) {
          dotClass = "sala__dot--gray";
          statusLabel = "Buit";
          statusClass = "sala__status--buit";
        } else {
          dotClass = "sala__dot--gray";
          statusLabel = "...";
          statusClass = "sala__status--sin-senyal";
        }

        return {
          sala,
          dotClass,
          statusLabel,
          statusClass,
          statusCompact,
          statusFullText,
          ipsLine,
          ipsTooltip,
        };
      });
    });

    function buildStatusText(s: SalaStatus, compact: boolean): string {
      const parts: string[] = [];
      if (s.nombreDifunto) parts.push(s.nombreDifunto);

      if (s.slideCount > 0) {
        parts.push(`${s.slideCount} diapos.`);
      } else if (s.nombreDifunto) {
        parts.push("Sense diapositives");
      }

      if (s.diapoPendentCount) {
        const pendLabel = compact ? "pend." : "pendents";
        parts.push(`⚠ ${s.diapoPendentCount} ${pendLabel}`);
      }

      parts.push(`fa ${s.secondsAgo}s`);
      return parts.join(" · ");
    }

    function buildCompactText(s: SalaStatus): string {
      return buildStatusText(s, true);
    }

    function buildFullText(s: SalaStatus): string {
      return buildStatusText(s, false);
    }

    function buildIpsLine(entries: HeartbeatEntry[]): string {
      return entries.map((e) => e.ip).join(", ");
    }

    function buildIpsTooltip(entries: HeartbeatEntry[]): string {
      return entries
        .map((e) => `${e.ip} · ${e.status} · ${e.message} · ${e.lastPollTime}`)
        .join("\n");
    }

    function fetchStatus() {
      axios
        .get("/api/sala/status")
        .then((response) => {
          salasStatus.value = response.data;
        })
        .catch(() => {});
    }

    onMounted(() => {
      erpClient
        .request(() => axios.get("/api/vetlla/index"))
        .then((response) => {
          vetllas = response.data;

          const now = new Date().getTime();
          for (const vetlla of vetllas) {
            const fechaFinDate = dateFromFechaString(vetlla.FechaFin);
            const fechaFinMs = fechaFinDate.getTime();

            if (!isNaN(fechaFinMs) && fechaFinMs > now) {
              vetllasCurso.value.push(vetlla);
            }
          }
        });

      const getVetllasAsignadas = (salas: Sala[]) =>
        salas.map((s) => s.IdVetlla);

      erpClient
        .request(() => axios.get("/api/sala/index"))
        .then((response) => {
          salas.value = response.data;
          vetllasAsignadas.value = getVetllasAsignadas(salas.value);

          updateExpiredVetllasInSales();

          for (const sala of salas.value) {
            watch(sala, () => {
              vetllasAsignadas.value = getVetllasAsignadas(salas.value);
              erpClient
                .request(() =>
                  axios.post("/api/sala/update", {
                    IdSala: sala.Id,
                    IdVetlla: sala.IdVetlla,
                  }),
                )
                .catch(() => {
                  CustomSwal.fire({
                    icon: "error",
                    title:
                      "No s'ha pogut actualitzar la vetlla per aquesta sala. Posi's en contacte amb els administradors de l'aplicació.",
                  });
                });
            });
          }
        });

      fetchStatus();
      statusIntervalId = setInterval(fetchStatus, 3000);
    });

    onBeforeUnmount(() => {
      clearInterval(statusIntervalId);
    });

    const opcionesVetlla = computed(() => {
      return vetllasCurso.value.map((v) => {
        if (vetllasAsignadas.value.includes(v.Id)) {
          return {
            disabled: true,
            vetlla: v,
          };
        } else {
          return {
            disabled: false,
            vetlla: v,
          };
        }
      });
    });

    const goPresentation = (id: Sala["Id"]) => {
      router.push({
        name: "presentationSala",
        params: { idSala: id },
      });
    };

    return {
      filasSala,
      goPresentation,
      opcionesVetlla,
    };
  },
});
</script>

<style lang="scss" scoped>
.card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2em;

  padding: 1em;
  border-radius: $rounded-border;

  background-color: $bg-color;
}

.salas {
  border-collapse: separate;
  border-spacing: 0 0.75em;

  tr {
    background-color: $bg-color;
    padding: 0.5em 0;
  }
}

.sala__nombre-cell {
  width: 9em;
  text-align: right;
  padding-right: 0.75em;
  vertical-align: middle;
}

.sala__nombre {
  display: inline-block;
  padding: 0.2em;
  border: 2px solid $primary-color;
  border-radius: $rounded-border;
  color: $primary-color;
  font-size: 1.4em;
}

.sala__vetlla-cell {
  width: 16em;
  vertical-align: middle;
}

.sala__vetlla {
  width: 100%;
  height: 3em;
  padding: 0.6em;
  border: $input-border;
  border-radius: $input-border-radius;
}

.sala__actions-cell {
  width: 1%;
  white-space: nowrap;
  padding-left: 0.75em;
  vertical-align: middle;
}

.sala__status-cell {
  padding-left: 0.5em;
  vertical-align: middle;
}

.sala__status {
  font-size: 0.82em;
  white-space: nowrap;
  display: block;

  &--activa {
    color: #2ecc71;
  }

  &--lenta {
    color: #f1c40f;
  }

  &--error {
    color: #e74c3c;
  }

  &--sin-senyal,
  &--buit {
    color: #95a5a6;
  }
}

.sala__ips {
  display: block;
  font-size: 0.7em;
  color: #95a5a6;
  white-space: nowrap;
}

.sala__dot {
  display: inline-block;
  width: 0.6em;
  height: 0.6em;
  border-radius: 50%;
  margin-left: 0.3em;
  vertical-align: middle;

  &--green {
    background-color: #2ecc71;
  }

  &--yellow {
    background-color: #f1c40f;
  }

  &--red {
    background-color: #e74c3c;
  }

  &--gray {
    background-color: #95a5a6;
  }
}

.sala-button {
  @include icon-button($primary-bg-color);

  &--primary {
    @include icon-button($primary-color);
  }
}
</style>
